import { isEmailValid } from './email';

export interface ValidationRule {
  validate: (value: string) => boolean;
  message: string;
}

export interface FieldValidation {
  value: string;
  rules: ValidationRule[];
  touched?: boolean;
}

export const ValidationRules = {
  required: (fieldName?: string, message?: string): ValidationRule => ({
    validate: (value: string) => value.trim().length > 0,
    message: message || `${fieldName || 'This field'} is required`,
  }),

  email: (message?: string): ValidationRule => ({
    validate: (value: string) => {
      return isEmailValid(value);
    },
    message: message || 'Please enter a valid email address',
  }),

  minLength: (min: number, fieldName?: string): ValidationRule => ({
    validate: (value: string) => value.length >= min,
    message: `${fieldName || 'This field'} must be at least ${min} characters`,
  }),

  maxLength: (max: number, fieldName?: string): ValidationRule => ({
    validate: (value: string) => value.length <= max,
    message: `${fieldName || 'This field'} must not exceed ${max} characters`,
  }),

  pattern: (regex: RegExp, message: string): ValidationRule => ({
    validate: (value: string) => regex.test(value),
    message,
  }),

  cardholderName: (message?: string): ValidationRule => ({
    validate: (value: string) => {
      // At least 2 characters, contains letters
      return value.trim().length >= 2 && /[a-zA-Z]/.test(value);
    },
    message: message || 'Please enter a valid cardholder name',
  }),

  zipCode: (): ValidationRule => ({
    validate: (value: string) => {
      // US zip code format: 5 digits or 5+4 digits
      return /^\d{5}(-\d{4})?$/.test(value);
    },
    message: 'Please enter a valid zip code',
  }),

  phoneNumber: (): ValidationRule => ({
    validate: (value: string) => {
      // Basic phone validation - at least 10 digits
      const digitsOnly = value.replace(/\D/g, '');
      return digitsOnly.length >= 10;
    },
    message: 'Please enter a valid phone number',
  }),
};

export const validateField = (value: string, rules: ValidationRule[]): string | null => {
  for (const rule of rules) {
    if (!rule.validate(value)) {
      return rule.message;
    }
  }
  return null;
};

export const validateFields = (fields: Record<string, FieldValidation>): Record<string, string> => {
  const errors: Record<string, string> = {};

  Object.entries(fields).forEach(([key, field]) => {
    const error = validateField(field.value, field.rules);
    if (error) {
      errors[key] = error;
    }
  });

  return errors;
};

export const hasErrors = (errors: Record<string, string>): boolean => {
  return Object.keys(errors).length > 0;
};

export const MIN_VALID_ADDRESS_LENGTH = 7;
